/* Contrato dos documentos legais versionados (ADR-0774). Uma linha por (docKey, version, locale):
   o "documento" é a CHAVE (`terms`/`privacy`), não uma entidade — e uma VERSÃO é o conjunto de
   linhas de idioma que compartilham (docKey, version). Três invariantes que este contrato carrega:

   1. `version` é INTEIRO monotônico por docKey e é calculado NO SERVIDOR — por isso não existe
      campo `version` em nenhum body de escrita (§2 da ADR).
   2. Versão `published` é IMUTÁVEL: o update só aceita `title`/`content` e a borda HTTP responde
      409 em linha publicada (§3). Corrigir texto publicado = criar a versão N+1, com
      `copyFromVersion` para não reescrever tudo à mão.
   3. A leitura pública devolve a versão VIGENTE com `locale` (o servido) E `requestedLocale` (o
      pedido): eles divergem quando o fallback pt-BR DENTRO da mesma versão entrou em ação (§4) —
      a superfície precisa dos dois para avisar "tradução indisponível".

   Compartilhado entre admin-service (validação de input + saída) e admin-web; a LP e o app leem o
   `publicLegalDocumentDto` pelos seus próprios clients. */
import { z } from 'zod';

/** Documentos legais existentes. Extensível: um `docKey` novo é só um valor a mais aqui. */
export const LEGAL_DOC_KEYS = ['terms', 'privacy'] as const;
export const legalDocKeySchema = z.enum(LEGAL_DOC_KEYS);
export type LegalDocKey = z.infer<typeof legalDocKeySchema>;

/* Sem `yanked` (≠ `app_releases`): "desfazer" texto legal não é tirar do ar — é publicar a versão
   N+1 que o substitui. Documento legal sem versão vigente não é estado válido do produto. */
export const LEGAL_STATUSES = ['draft', 'published'] as const;
export const legalStatusSchema = z.enum(LEGAL_STATUSES);
export type LegalStatus = z.infer<typeof legalStatusSchema>;

/* Idiomas canônicos da edição (abas do form). O modelo aceita qualquer `locale` (i18n por linha). */
export const LEGAL_LOCALES = ['pt-BR', 'en-US'] as const;
export type LegalLocale = (typeof LEGAL_LOCALES)[number];

/** Locale de fallback DENTRO da versão vigente (§4): idioma faltando NUNCA rebaixa a versão. */
export const LEGAL_FALLBACK_LOCALE: LegalLocale = 'pt-BR';

const TITLE_MAX_LEN = 300;
/* Termos + política são documentos longos em markdown; o cap é anti-abuso de payload (análogo ao
   `ANSWER_MAX_LEN` do FAQ), não uma regra de negócio. */
const CONTENT_MAX_LEN = 400_000;

const localeSchema = z.string().min(2).max(10);
const titleSchema = z.string().min(1).max(TITLE_MAX_LEN);
const contentSchema = z.string().min(1).max(CONTENT_MAX_LEN);
/* Versão inteira monotônica ≥ 1 (§2). Aparece em DTO de saída e em `copyFromVersion` — nunca como
   campo de identidade num body de criação. */
const versionNumberSchema = z.number().int().positive();

/** Uma linha (docKey, version, locale) — visão de GESTÃO (inclui draft + metadados). */
export const legalVersionDto = z.object({
  id: z.string().min(1),
  docKey: legalDocKeySchema,
  version: versionNumberSchema,
  locale: localeSchema,
  title: titleSchema,
  content: contentSchema,
  status: legalStatusSchema,
  /* Quando ESTA versão passa a vigorar (fato jurídico). `null` em draft. */
  effectiveAt: z.string().nullable(),
  /* Quando o staff publicou (fato administrativo). `null` em draft. */
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type LegalVersion = z.infer<typeof legalVersionDto>;

/** Envelope de lista/criação/publicação (uma versão = N linhas de idioma). */
export const legalVersionListDto = z.object({ versions: z.array(legalVersionDto) });
export type LegalVersionList = z.infer<typeof legalVersionListDto>;

/** Envelope de linha única (get/update/delete). */
export const legalVersionDetailDto = z.object({ version: legalVersionDto });
export type LegalVersionDetail = z.infer<typeof legalVersionDetailDto>;

/** Visão PÚBLICA da versão VIGENTE (LP anônima + rodapé do login + app). `locale` é o
    efetivamente servido e `requestedLocale` o pedido: divergem quando o fallback da §4 agiu.
    `effectiveAt` é non-null aqui — só versão vigente chega nesta superfície. */
export const publicLegalDocumentDto = z.object({
  docKey: legalDocKeySchema,
  version: versionNumberSchema,
  locale: localeSchema,
  requestedLocale: localeSchema,
  title: titleSchema,
  content: contentSchema,
  effectiveAt: z.string(),
  publishedAt: z.string().nullable(),
});
export type PublicLegalDocument = z.infer<typeof publicLegalDocumentDto>;

/** Entrada do índice público (sem o markdown): alimenta rodapés/menus de link. */
export const legalIndexEntryDto = z.object({
  docKey: legalDocKeySchema,
  version: versionNumberSchema,
  locale: localeSchema,
  requestedLocale: localeSchema,
  title: titleSchema,
  effectiveAt: z.string(),
});
export type LegalIndexEntry = z.infer<typeof legalIndexEntryDto>;

export const legalIndexDto = z.object({ documents: z.array(legalIndexEntryDto) });
export type LegalIndex = z.infer<typeof legalIndexDto>;

/* Query da leitura pública: `locale` explícito (sem negociação por `Accept-Language` — as duas
   superfícies sabem o idioma que querem). */
export const publicLegalQuery = z.object({
  locale: localeSchema.default(LEGAL_FALLBACK_LOCALE),
});
export type PublicLegalQuery = z.infer<typeof publicLegalQuery>;

/** Conteúdo de UM idioma de uma versão. */
export const legalVersionContentDto = z.object({
  locale: localeSchema,
  title: titleSchema,
  content: contentSchema,
});
export type LegalVersionContent = z.infer<typeof legalVersionContentDto>;

/* Criação de versão. NÃO existe campo `version`: o servidor calcula `max(version) + 1` do docKey
   (§2), então um `version` enviado pelo cliente é descartado pelo strip do Zod. Duas formas
   mutuamente exclusivas de nascer: conteúdo explícito (`locales`) ou cópia de uma versão anterior
   (`copyFromVersion` — ergonomia do typo, §3: o resultado é uma versão nova de verdade). A nova
   versão nasce SEMPRE `draft` (publicar é passo próprio). */
export const createLegalVersionBody = z
  .object({
    locales: z.array(legalVersionContentDto).min(1).optional(),
    copyFromVersion: versionNumberSchema.optional(),
  })
  .refine((b) => (b.locales === undefined) !== (b.copyFromVersion === undefined), {
    message: 'legal_create_needs_locales_xor_copy_from',
  })
  .refine(
    (b) =>
      b.locales === undefined || new Set(b.locales.map((l) => l.locale)).size === b.locales.length,
    { message: 'legal_locales_duplicated' },
  );
export type CreateLegalVersionBody = z.infer<typeof createLegalVersionBody>;

/* Update de linha DRAFT: objeto explícito com `.optional()` (nunca `.partial()`). Só conteúdo —
   `docKey`/`version`/`locale` são identidade e `status`/`effectiveAt` mudam pelo publish. */
export const updateLegalVersionBody = z.object({
  title: titleSchema.optional(),
  content: contentSchema.optional(),
});
export type UpdateLegalVersionBody = z.infer<typeof updateLegalVersionBody>;

/* Publicação: `effectiveAt` ausente = vigora agora. Data futura é o caso normal de compliance
   ("publicar hoje, vigorar em 30 dias") — a versão fica no banco `published` e invisível para a
   leitura pública até o relógio passar, sem cron (§4). */
export const publishLegalVersionBody = z.object({
  effectiveAt: z.coerce.date().optional(),
});
export type PublishLegalVersionBody = z.infer<typeof publishLegalVersionBody>;

/** O lote legal só remove drafts; publicar continua uma decisão individual e irreversível. */
export const LEGAL_BULK_MAX_VERSIONS = 100;
export const bulkDeleteLegalDraftsBody = z.object({
  ids: z
    .array(z.string().min(1))
    .min(1)
    .max(LEGAL_BULK_MAX_VERSIONS)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'duplicate_bulk_ids' }),
  action: z.literal('deleteDrafts'),
});
export type BulkDeleteLegalDraftsBody = z.infer<typeof bulkDeleteLegalDraftsBody>;

export const bulkDeleteLegalDraftsResponseDto = z.object({
  results: z.array(z.object({ id: z.string(), ok: z.boolean(), error: z.string().nullable() })),
});
export type BulkDeleteLegalDraftsResponse = z.infer<typeof bulkDeleteLegalDraftsResponseDto>;

/* Body do seed (rota interna): a v1 de cada documento, já publicada e vigente. Idempotente por
   (docKey, version, locale) — rodar N vezes não duplica nem sobrescreve o que o staff editou. */
export const seedLegalBody = z.object({
  documents: z
    .array(
      z.object({
        docKey: legalDocKeySchema,
        locales: z.array(legalVersionContentDto).min(1),
      }),
    )
    .min(1),
});
export type SeedLegalBody = z.infer<typeof seedLegalBody>;
